import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './text-editor.scss';
import {
    BG_MODE,
    DEFAULT_BG_MODE,
    DEFAULT_TEXT_COLOR,
    FONT,
    TEXT_COLORS,
    clamp,
    createTextItem,
    drawTextItemOnCanvas,
    getBgButtonLabel,
    getNextBgMode,
    initializeTextItems,
    resolveTextInputStyle,
    useImageDisplayArea,
    useTextDrag,
} from './utils';

const TextEditor = ({ file, imgSrc, onEditComplete, onRegisterApply, initialTextItems, onTextItemsChange }) => {
    const editorContainerRef = useRef(null);
    const mediaElementRef = useRef(null);
    const applyFnRef = useRef(null);
    const textInputRefs = useRef({});
    const textItemDivRefs = useRef({});
    const resizeState = useRef(null);
    const domContentPopulated = useRef(false);

    const { current: seedItems } = useRef(initializeTextItems(initialTextItems));

    const [textItems, setTextItems] = useState(seedItems);
    const [activeTextId, setActiveTextId] = useState(seedItems[0]?.id ?? null);
    const [preferredBgMode, setPreferredBgMode] = useState(DEFAULT_BG_MODE);

    const activeTextItem = textItems.find(item => item.id === activeTextId) ?? null;
    const canAddNewText = textItems.every(item => item.content.trim().length > 0);
    const currentBgMode = activeTextItem?.bgMode ?? preferredBgMode;

    const { displayArea, onMediaLoad } = useImageDisplayArea(editorContainerRef, mediaElementRef);

    const handlePositionChange = (itemId, newPosition) => {
        setTextItems(prev => prev.map(item => (item.id === itemId ? { ...item, position: newPosition } : item)));
    };

    const { onDragStart, onDragMove, onDragEnd } = useTextDrag(
        editorContainerRef,
        displayArea,
        handlePositionChange,
        setActiveTextId,
    );

    useEffect(() => {
        if (!displayArea || domContentPopulated.current) return;
        domContentPopulated.current = true;

        textItems.forEach(item => {
            const el = textInputRefs.current[item.id];
            if (el && item.content && el.textContent !== item.content) {
                el.textContent = item.content;
            }
        });

        const focusTargetId = activeTextId ?? textItems[0]?.id;
        setTimeout(() => textInputRefs.current[focusTargetId]?.focus(), 100);
    }, [displayArea]); // eslint-disable-line react-hooks/exhaustive-deps

    applyFnRef.current = () =>
        new Promise(resolve => {
            if (!imgSrc) {
                resolve(null);
                return;
            }

            const image = new window.Image();
            image.src = imgSrc;
            image.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = image.naturalWidth;
                canvas.height = image.naturalHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(image, 0, 0);

                const displayAreaWidth = displayArea?.width ?? canvas.width;

                textItems
                    .filter(item => item.content.trim())
                    .forEach(item => drawTextItemOnCanvas(ctx, item, canvas.width, canvas.height, displayAreaWidth));

                canvas.toBlob(
                    blob => {
                        if (!blob) {
                            resolve(null);
                            return;
                        }
                        const outputFile = new File([blob], file.name, { type: file.type, lastModified: Date.now() });
                        onTextItemsChange?.(textItems.filter(item => item.content.trim()));
                        onEditComplete?.(outputFile);
                        resolve(outputFile);
                    },
                    file.type || 'image/jpeg',
                    0.92,
                );
            };
        });

    useEffect(() => {
        onRegisterApply?.(async () => await applyFnRef.current?.());
        return () => onRegisterApply?.(null);
    }, [onRegisterApply]);

    const handleContainerBackgroundClick = event => {
        const clickedBackground =
            event.target === editorContainerRef.current || event.target.classList.contains('wa-te-bg');
        if (!clickedBackground) return;

        const focusedItem = textItems.find(item => item.id === activeTextId);
        if (focusedItem && !focusedItem.content.trim() && textItems.length > 1) {
            setTextItems(prev => prev.filter(item => item.id !== activeTextId));
        }
        setActiveTextId(null);
    };

    const handleAddText = () => {
        if (!canAddNewText) return;
        const newItem = createTextItem(DEFAULT_TEXT_COLOR, DEFAULT_BG_MODE, FONT.DEFAULT);
        setTextItems(prev => [...prev, newItem]);
        setActiveTextId(newItem.id);
        setTimeout(() => textInputRefs.current[newItem.id]?.focus(), 50);
    };

    const handleDeleteActiveText = () => {
        if (!activeTextId) return;
        const remaining = textItems.filter(item => item.id !== activeTextId);

        if (remaining.length === 0) {
            const replacement = createTextItem(DEFAULT_TEXT_COLOR, DEFAULT_BG_MODE, FONT.DEFAULT);
            setTextItems([replacement]);
            setActiveTextId(replacement.id);
            setTimeout(() => textInputRefs.current[replacement.id]?.focus(), 50);
        } else {
            setTextItems(remaining);
            setActiveTextId(remaining[remaining.length - 1].id);
        }
    };

    const handleColorSelect = color => {
        if (activeTextId) {
            setTextItems(prev => prev.map(item => (item.id === activeTextId ? { ...item, color } : item)));
        }
    };

    const handleBgModeToggle = () => {
        if (!activeTextItem) return;
        const nextBgMode = getNextBgMode(activeTextItem.bgMode);
        setPreferredBgMode(nextBgMode);
        setTextItems(prev => prev.map(item => (item.id === activeTextId ? { ...item, bgMode: nextBgMode } : item)));
    };

    const handleResizeStart = (event, item) => {
        event.stopPropagation();
        event.preventDefault();

        if (!item.content.trim()) return;

        const itemEl = textItemDivRefs.current[item.id];
        if (!itemEl) return;

        const rect = itemEl.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const source = event.touches ? event.touches[0] : event;
        const startDist = Math.hypot(source.clientX - centerX, source.clientY - centerY);

        resizeState.current = {
            itemId: item.id,
            centerX,
            centerY,
            startDist: Math.max(startDist, 10),
            startFontSize: item.fontSize ?? FONT.DEFAULT,
        };
    };

    const handleResizeMove = event => {
        if (!resizeState.current) return;

        const { centerX, centerY, startDist, startFontSize, itemId } = resizeState.current;
        const source = event.touches ? event.touches[0] : event;
        const currentDist = Math.hypot(source.clientX - centerX, source.clientY - centerY);
        const newSize = clamp(Math.round(startFontSize * (currentDist / startDist)), FONT.MIN, FONT.MAX);

        setTextItems(prev => prev.map(item => (item.id === itemId ? { ...item, fontSize: newSize } : item)));
    };

    const handleResizeEnd = () => {
        resizeState.current = null;
    };

    const handleTextItemFocus = itemId => setActiveTextId(itemId);

    const handleTextItemInput = (event, itemId) => {
        const content = event.currentTarget.textContent || '';
        setTextItems(prev => prev.map(item => (item.id === itemId ? { ...item, content } : item)));
    };

    const bgButtonInlineStyle =
        currentBgMode === BG_MODE.COLORED ? { background: activeTextItem?.color, color: '#fff' } : {};

    return (
        <div className="se-text-editor">
            <div
                className="se-text-wrapper"
                ref={editorContainerRef}
                onClick={handleContainerBackgroundClick}
                onMouseMove={e => {
                    onDragMove(e);
                    handleResizeMove(e);
                }}
                onMouseUp={() => {
                    onDragEnd();
                    handleResizeEnd();
                }}
                onMouseLeave={() => {
                    onDragEnd();
                    handleResizeEnd();
                }}
                onTouchMove={e => {
                    onDragMove(e);
                    handleResizeMove(e);
                }}
                onTouchEnd={() => {
                    onDragEnd();
                    handleResizeEnd();
                }}
            >
                <img
                    ref={mediaElementRef}
                    src={imgSrc}
                    alt="preview"
                    className="se-te-bg"
                    draggable={false}
                    onLoad={onMediaLoad}
                />

                {displayArea &&
                    textItems.map(item => (
                        <div
                            key={item.id}
                            ref={el => {
                                textItemDivRefs.current[item.id] = el;
                            }}
                            className={`se-te-item${item.id === activeTextId ? ' active' : ''}`}
                            style={{
                                position: 'absolute',
                                left: displayArea.left + (item.position.x / 100) * displayArea.width,
                                top: displayArea.top + (item.position.y / 100) * displayArea.height,
                                transform: 'translate(-50%, -50%)',
                            }}
                            onMouseDown={e => onDragStart(e, item.id, item.position)}
                            onTouchStart={e => onDragStart(e, item.id, item.position)}
                        >
                            <p
                                ref={el => {
                                    textInputRefs.current[item.id] = el;
                                }}
                                contentEditable
                                suppressContentEditableWarning
                                className="se-te-input"
                                data-placeholder="Add text"
                                style={resolveTextInputStyle(item)}
                                onFocus={() => handleTextItemFocus(item.id)}
                                onInput={e => handleTextItemInput(e, item.id)}
                            />
                            {item.id === activeTextId && item.content.trim().length > 0 && (
                                <div
                                    className="se-te-resize-handle"
                                    onMouseDown={e => handleResizeStart(e, item)}
                                    onTouchStart={e => handleResizeStart(e, item)}
                                />
                            )}
                        </div>
                    ))}
            </div>
            <div className="se-te-bottom">
                <button
                    type="button"
                    className={`se-te-tool-btn bg-${currentBgMode}`}
                    style={bgButtonInlineStyle}
                    onClick={handleBgModeToggle}
                    title="Toggle background style"
                >
                    {getBgButtonLabel(currentBgMode)}
                </button>

                <div className="se-te-palette">
                    {TEXT_COLORS.map(color => (
                        <button
                            key={color}
                            type="button"
                            className={`se-te-color-dot${activeTextItem?.color === color ? ' active' : ''}`}
                            style={{ background: color }}
                            onClick={() => handleColorSelect(color)}
                            aria-label={`Select color ${color}`}
                        />
                    ))}
                </div>

                <button
                    type="button"
                    className={`se-te-tool-btn${!activeTextId ? ' se-te-hidden' : ''}`}
                    onClick={handleDeleteActiveText}
                    aria-label="Delete text"
                >
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                </button>
                <button
                    type="button"
                    className={`se-te-add-btn${!canAddNewText ? ' se-te-disabled' : ''}`}
                    onClick={handleAddText}
                    title={canAddNewText ? 'Add another text' : 'Type some text first'}
                >
                    + Add
                </button>
            </div>
        </div>
    );
};

TextEditor.propTypes = {
    file: PropTypes.object.isRequired,
    imgSrc: PropTypes.string.isRequired,
    onEditComplete: PropTypes.func,
    onRegisterApply: PropTypes.func,
    initialTextItems: PropTypes.array,
    onTextItemsChange: PropTypes.func,
};

export default TextEditor;
